import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import OrderModal from '../components/OrderModal';
/* ================== OUTER (COSMIC) ================== */

const Page = styled.div`
  box-sizing: border-box;

  /* ВАЖНО: ширина auto = занимает всю доступную ширину с учетом margin */
  width: auto;
  max-width: 1800px;

  /* один источник истины для отступов: меняем только margin */
  margin: 18px auto;

  min-height: 100dvh;
  padding: clamp(12px, 2.8vw, 28px);
  color: #fff;
  border-radius: 50px;
  overflow: hidden;

  /* Tablet */
  @media (max-width: 800px) {
    margin: 10px auto;
    border-radius: 34px;
    padding: 18px;
  }

  /* Mobile */
  @media (max-width: 480px) {
    margin: 8px auto; /* вот тут Page станет "как другие" */
    border-radius: 22px;
    padding: 14px 12px;

    padding-bottom: calc(14px + env(safe-area-inset-bottom));
    padding-left: calc(12px + env(safe-area-inset-left));
    padding-right: calc(12px + env(safe-area-inset-right));
  }

  background:
    radial-gradient(1200px 700px at 50% 10%, rgba(226, 109, 0, 0.75), transparent 60%),
    radial-gradient(900px 600px at 20% 35%, rgba(120, 180, 255, 0.14), transparent 60%),
    linear-gradient(180deg, rgba(0, 0, 0, 0.35), rgba(240, 8, 8, 0.65)),
    var(--bg) center / cover no-repeat,
    linear-gradient(180deg, #050a12, #03050a);
`;

const Wrap = styled.div`
  width: min(100%, 1000px);
  margin: 0 auto;
  display: grid;
  gap: clamp(18px, 2.4vw, 26px);
`;

/* ================== HERO ================== */

const Hero = styled.section`
  padding: clamp(34px, 5vw, 70px) 10px clamp(18px, 3vw, 28px);
  text-align: center;
  display: grid;
  gap: 10px;

  @media (max-width: 480px) {
    padding: 26px 6px 14px;
    gap: 8px;
  }
`;

const H1 = styled.h1`
  margin: 0;
  font-weight: 900;
  letter-spacing: -0.02em;
  font-size: clamp(34px, 4.8vw, 68px);
  text-shadow: 0 10px 40px rgba(0, 0, 0, 0.55);
`;

const Sub = styled.p`
  margin: 0 auto;
  max-width: 760px;
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(14px, 1.7vw, 18px);
  line-height: 1.6;
  white-space: pre-line;
`;

/* ================== GLASS CARDS ================== */

const Cards = styled.section`
  display: grid;
  gap: clamp(12px, 2vw, 18px);

  /* Mobile default: 1 column */
  grid-template-columns: 1fr;

  /* Tablet: 2 columns */
  @media (min-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Desktop: 3 columns */
  @media (min-width: 980px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const GlassCard = styled.a`
  text-decoration: none;
  color: inherit;
  border-radius: 22px;
  padding: 18px 18px 16px;
  position: relative;
  overflow: hidden;

  background: rgba(20, 26, 40, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);

  display: grid;
  gap: 12px;
  min-height: 170px;

  transition:
    transform 160ms ease,
    border-color 160ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.22);
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 138, 31, 0.35);
    outline-offset: 4px;
  }

  @media (max-width: 480px) {
    min-height: 150px;
    padding: 14px 14px 12px;
    border-radius: 18px;
    gap: 10px;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -40px;
    background:
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1), transparent 28%),
      radial-gradient(circle at 70% 55%, rgba(255, 255, 255, 0.08), transparent 30%),
      radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.06), transparent 26%);
    opacity: 0.55;
    pointer-events: none;
  }
`;

const CardTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BigIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  overflow: hidden;

  svg {
    width: 30px;
    height: 30px;
    opacity: 0.95;
  }
`;

const IconImg = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  display: block;
  opacity: 0.95;
`;

const CardName = styled.div`
  position: relative;
  z-index: 1;
  font-weight: 900;
  font-size: 20px;
  letter-spacing: -0.01em;
`;

const Line = styled.div`
  position: relative;
  z-index: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.14);
`;

const CardValue = styled.div`
  position: relative;
  z-index: 1;
  font-weight: 900;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.92);
  overflow-wrap: anywhere;
`;

const CardHint = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
  color: rgba(255, 255, 255, 0.72);
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const External = styled.span`
  opacity: 0.75;
`;

/* ================== FOOT TEXTS ================== */

const Reply = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(14px, 1.6vw, 18px);
  line-height: 1.6;
  padding: 10px 0 22px;

  strong {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const Bottom = styled.section`
  text-align: center;
  padding: 10px 0 6px;
  color: rgba(255, 255, 255, 0.74);
  font-size: clamp(14px, 1.6vw, 18px);

  strong {
    color: rgba(255, 255, 255, 0.94);
  }
`;

const BottomCTA = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: center;
`;

const OrderBtn = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 16px;
  padding: 12px 18px;
  font-weight: 950;
  background: #ff8a1f;
  color: #1a120a;
  box-shadow: 0 18px 50px rgba(255, 138, 31, 0.22);
  transition:
    transform 160ms ease,
    filter 160ms ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }
`;

/* ================== ICONS (KEEP EXISTING) ================== */

const TelegramIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M18.9 3.5 2.7 9.8c-1.1.4-1.1 1.1-.2 1.4l4.1 1.3 9.5-6c.4-.3.8-.1.5.2l-7.7 7 3.3 2.4c.6.4 1 .2 1.2-.5l2.1-9.8c.3-.8-.3-1.3-.9-1.1Z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.5 3.5A11 11 0 0 0 3.2 17.9L2 22l4.2-1.1A11 11 0 0 0 20.5 3.5Zm-8.9 18a9.2 9.2 0 0 1-4.7-1.3l-.3-.2-2.4.6.6-2.3-.2-.3A9.2 9.2 0 1 1 11.6 21.5Zm5.4-6.9c-.3-.2-1.8-.9-2.1-1s-.5-.2-.7.2-.8 1-.9 1.2-.3.2-.6.1a7.6 7.6 0 0 1-2.2-1.3 8.3 8.3 0 0 1-1.5-1.9c-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6s-.7-1.8-.9-2.4c-.2-.6-.4-.5-.7-.5h-.6c-.2 0-.6.1-.9.4s-1.2 1.1-1.2 2.8 1.2 3.2 1.4 3.5 2.3 3.6 5.6 5c.8.3 1.4.5 1.9.7.8.2 1.5.2 2.1.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4s-.2-.2-.5-.4Z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5v2Z" />
  </svg>
);

/* ================== helpers ================== */

const absMedia = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `http://127.0.0.1:8000${url}`;
};

const SafeHtml = ({ html }) => <span dangerouslySetInnerHTML={{ __html: html || '' }} />;

const BuiltinIcon = ({ name }) => {
  if (name === 'telegram') return <TelegramIcon />;
  if (name === 'whatsapp') return <WhatsAppIcon />;
  return <EmailIcon />;
};

const InlineSvg = ({ svg }) => (
  <span
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: svg || '' }}
    style={{ display: 'grid', placeItems: 'center' }}
  />
);

/* ✅ FIX "/api/api/..." */
function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

/* ================== PAGE ================== */

export default function Contacts({ onOpenOrder }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [orderOpen, setOrderOpen] = useState(false);
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

  // pick(obj, "hero_title") -> obj.hero_title_it / obj.hero_title_en
  const pick = React.useCallback(
    (obj, key) => {
      if (!obj) return '';
      return (lang === 'it' ? obj[`${key}_it`] : obj[`${key}_en`]) || obj[`${key}_en`] || '';
    },
    [lang],
  );

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // было: api.get('contacts/page/')
        const res = await api.get(safeApiPath('/contacts/page/'));
        if (!alive) return;
        setData(res.data);
      } catch (e) {
        console.error('Contacts load error:', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const d = data || {};
  const bgUrl = absMedia(d.bg_image_url);
  const cards = Array.isArray(d.cards) ? d.cards : [];

  return (
    <Page style={{ ['--bg']: bgUrl ? `url(${bgUrl})` : 'none' }}>
      <SEO
        title="Contacts"
        description="Contact information and details for our company."
        path="/contacts"
      />

      <Wrap>
        <Hero>
          <H1>{loading ? '...' : pick(d, 'hero_title') || 'Contacts'}</H1>
          <Sub>{loading ? '' : pick(d, 'hero_subtitle') || ''}</Sub>
        </Hero>

        <Cards>
          {cards.map((c) => {
            const href = c.href || '#';
            const targetProps = c.open_in_new_tab ? { target: '_blank', rel: 'noreferrer' } : {};
            const aria = c.name ? `Open ${c.name}` : 'Open contact';

            const iconFileUrl = absMedia(c.icon_file_url);

            const cardName = pick(c, 'name') || c.name || 'Contact';
            const cardValue = pick(c, 'value') || c.value || '';
            const cardHint = pick(c, 'hint') || c.hint || '';

            return (
              <GlassCard key={c.id} href={href} aria-label={aria} {...targetProps}>
                <CardTop>
                  <BigIcon>
                    {c.icon_kind === 'builtin' ? (
                      <BuiltinIcon name={c.builtin_icon} />
                    ) : c.icon_kind === 'upload' && iconFileUrl ? (
                      <IconImg src={iconFileUrl} alt="" />
                    ) : c.icon_kind === 'inline_svg' && c.inline_svg ? (
                      <InlineSvg svg={c.inline_svg} />
                    ) : (
                      <EmailIcon />
                    )}
                  </BigIcon>

                  <CardName>{cardName}</CardName>
                </CardTop>

                <Line />

                <CardValue>{cardValue}</CardValue>

                <CardHint>
                  <span>{cardHint}</span>
                  <External>↗</External>
                </CardHint>
              </GlassCard>
            );
          })}
        </Cards>

        <Reply>
          <SafeHtml html={pick(d, 'reply_text') || d.reply_text || ''} />
        </Reply>

        <Bottom>
          <SafeHtml html={pick(d, 'bottom_text') || d.bottom_text || ''} />

          <BottomCTA onClick={() => setOrderOpen(true)}>
            <OrderBtn type="button">
              {pick(d, 'order_button_text') || d.order_button_text || 'Open order form'}
            </OrderBtn>
          </BottomCTA>
        </Bottom>
        <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />
      </Wrap>
    </Page>
  );
}
